import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { feedbackManagementService, Feedback, ListFeedbackParams } from '../services/feedbackManagement';
import './FeedbackManagementPage.css';
import { Table, TableColumn } from '../components/Table';

export const FeedbackManagementPage: React.FC = () => {
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [, setTotalCount] = useState(0);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>();
  const [viewingFeedback, setViewingFeedback] = useState<string | null>(null);
  const itemsPerPage = 50;

  const loadFeedback = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListFeedbackParams = {
        limit: itemsPerPage,
        search: searchTerm || undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        rating: ratingFilter !== 'all' ? parseInt(ratingFilter) : undefined,
      };

      if (currentPage > 1 && lastEvaluatedKey) {
        params.lastEvaluatedKey = lastEvaluatedKey;
      }

      const response = await feedbackManagementService.listFeedback(params);
      setFeedback(response.feedback);
      setTotalCount(response.count);
      setLastEvaluatedKey(response.lastEvaluatedKey);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load feedback');
      console.error('Error loading feedback:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- lastEvaluatedKey intentionally omitted
  }, [currentPage, categoryFilter, ratingFilter, searchTerm]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
    loadFeedback();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setRatingFilter('all');
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
  };

  const handleExport = () => {
    const csvContent = [
      ['Feedback ID', 'User ID', 'Category', 'Rating', 'Comment', 'Created'].join(','),
      ...feedback.map(f => [
        f.feedbackId,
        f.userId,
        f.category,
        f.rating,
        f.comment || '',
        f.createdAt,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `feedback-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const stats = useMemo(() => {
    const total = feedback.length;
    const avgRating = total > 0 ? feedback.reduce((sum, f) => sum + f.rating, 0) / total : 0;
    const categoryBreakdown = feedback.reduce((acc, f) => {
      acc[f.category] = (acc[f.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return { total, avgRating, categoryBreakdown };
  }, [feedback]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns: TableColumn<Feedback>[] = useMemo(() => [
    {
      header: 'Feedback ID',
      accessor: 'feedbackId',
      render: (value) => <div className="feedback-id">{(value as string).substring(0, 8)}...</div>,
    },
    {
      header: 'User ID',
      accessor: 'userId',
      render: (value) => <div className="secondary-text">{(value as string).substring(0, 8)}...</div>,
      hideOnTablet: true,
    },
    {
      header: 'Category',
      accessor: 'category',
      render: (value) => <span className="category-badge">{value as string}</span>,
    },
    {
      header: 'Rating',
      accessor: 'rating',
      render: (value) => (
        <div className="rating-display">
          {'★'.repeat(value as number)}{'☆'.repeat(5 - (value as number))}
        </div>
      ),
    },
    {
      header: 'Comment',
      accessor: 'comment',
      render: (value) => (
        <div className="comment-text">{(value as string)?.substring(0, 50) || 'No comment'}...</div>
      ),
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      render: (value) => formatDate(value as string),
    },
    {
      header: 'Actions',
      accessor: 'feedbackId',
      render: (value) => (
        <button
          onClick={() => setViewingFeedback(value as string)}
          className="view-button"
        >
          View
        </button>
      ),
    },
  ], []);

  return (
    <div className="feedback-management-page">
      <div className="page-header">
        <h1>Feedback Management</h1>
        <div className="header-actions">
          <div className="stats-summary">
            <div className="stat-item">
              <span className="stat-label">Total:</span>
              <span className="stat-value">{stats.total}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Avg Rating:</span>
              <span className="stat-value">{stats.avgRating.toFixed(1)}</span>
            </div>
          </div>
          <button onClick={handleExport} className="export-button">
            Export CSV
          </button>
        </div>
      </div>

      <div className="feedback-filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by comment..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">Search</button>
        </form>

        <div className="advanced-filters">
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setCurrentPage(1);
              setLastEvaluatedKey(undefined);
            }}
            className="filter-select"
          >
            <option value="all">All Categories</option>
            <option value="general">General</option>
            <option value="bug_report">Bug Report</option>
            <option value="feature_request">Feature Request</option>
            <option value="ui_ux">UI/UX</option>
            <option value="performance">Performance</option>
            <option value="other">Other</option>
          </select>

          <select
            value={ratingFilter}
            onChange={(e) => {
              setRatingFilter(e.target.value);
              setCurrentPage(1);
              setLastEvaluatedKey(undefined);
            }}
            className="filter-select"
          >
            <option value="all">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>

          <button onClick={handleClearFilters} className="clear-filters-button">
            Clear Filters
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading feedback...</div>
      ) : (
        <>
          <Table
            columns={columns}
            data={feedback}
            getRowId={(row) => row.feedbackId}
            emptyMessage="No feedback found"
            containerClassName="feedback-table-container"
            className="feedback-table"
          />

          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(currentPage - 1);
                setLastEvaluatedKey(undefined);
              }}
            >
              Previous
            </button>
            <span>Page {currentPage}</span>
            <button
              disabled={!lastEvaluatedKey || feedback.length < itemsPerPage}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {viewingFeedback && (
        <FeedbackDetailModal
          feedbackId={viewingFeedback}
          onClose={() => {
            setViewingFeedback(null);
            loadFeedback();
          }}
        />
      )}
    </div>
  );
};

const FeedbackDetailModal: React.FC<{ feedbackId: string; onClose: () => void }> = ({ feedbackId, onClose }) => {
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on feedbackId change only
  }, [feedbackId]);

  const loadFeedback = async () => {
    try {
      setLoading(true);
      const data = await feedbackManagementService.getFeedback(feedbackId);
      setFeedback(data);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to load feedback');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="loading">Loading feedback...</div>
        </div>
      </div>
    );
  }

  if (!feedback) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Feedback Details</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>
        <div className="modal-body">
          <div className="detail-section">
            <label>Feedback ID:</label>
            <div>{feedback.feedbackId}</div>
          </div>
          <div className="detail-section">
            <label>User ID:</label>
            <div>{feedback.userId}</div>
          </div>
          <div className="detail-section">
            <label>Category:</label>
            <div>{feedback.category}</div>
          </div>
          <div className="detail-section">
            <label>Rating:</label>
            <div>{'★'.repeat(feedback.rating)}{'☆'.repeat(5 - feedback.rating)} ({feedback.rating}/5)</div>
          </div>
          {feedback.comment && (
            <div className="detail-section">
              <label>Comment:</label>
              <div>{feedback.comment}</div>
            </div>
          )}
          <div className="detail-section">
            <label>Created:</label>
            <div>{new Date(feedback.createdAt).toLocaleString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
